/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The week grid — the app's primary screen.
 *
 * ── DATE HANDLING ───────────────────────────────────────────────────────────
 * The week shown is derived from the SERVER's today (via useToday), offset by a
 * week counter. This component never calls `new Date()`. `isToday` comes from
 * the server's own comparison, not a client-side one, so the highlight can't
 * disagree with the data.
 *
 * ── THREE THINGS CHANGED ABOUT HOW THIS SCREEN IS OPERATED ──────────────────
 *
 * 1. IT HAS A PRIMARY ACTION NOW. The header offered Print week / Add to
 *    calendar / Week tools, all ghost, all equal — while the reason the screen
 *    exists, adding a meal, was a dashed `+ Add` repeated once per slot per
 *    day. A new household is seeded with four slots, so the default week
 *    presented TWENTY-EIGHT identical affordances and no starting point. There
 *    is now one `Add a meal` button that asks which day first, and the three
 *    week-level tools live behind one menu.
 *
 * 2. THE CARD ACTIONS EXIST ON A PHONE. Edit, guests, mark-cooked and remove
 *    were four emoji in an `opacity-0 group-hover:opacity-100` cluster. Touch
 *    devices have no hover, so all four were unreachable there; the cluster
 *    also lacked `focus-within`, so a keyboard user tabbed into controls they
 *    could not see. Mark-cooked is now a permanent 44px toggle and the rest sit
 *    in a ⋯ menu that `.reveal` keeps visible wherever hover doesn't exist.
 *
 * 3. MOBILE SHOWS ONE DAY. Seven columns inside `max-w-6xl` is ~150px per day
 *    on a laptop, and below `lg` the grid collapsed into an undifferentiated
 *    1–2 column list of every meal in the week — which is neither a
 *    week-at-a-glance nor a usable day view. Mobile now shows a single day with
 *    the WeekRibbon above it for jumping; desktop keeps the grid.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PlanApi, RecipesApi } from "../../api/endpoints.ts";
import { addDays, formatDayNumber, formatRange, formatWeekday } from "../../lib/dates.ts";
import { useToday } from "../../lib/useToday.tsx";
import { AllergenBadge, Banner, Empty, ErrorBox, Loading, Modal, PageHeader } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { WeekRibbon } from "../../components/WeekRibbon.tsx";
import type { PlanEntry, RecipeSummary } from "../../api/types.ts";
import { WeekToolsModal } from "./WeekToolsModal.tsx";

export function PlanView() {
  const { today, weekStartFor, isLoading: dateLoading } = useToday();
  const [showTools, setShowTools] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [addingTo, setAddingTo] = useState<{ date: string; slot: string | null } | null>(null);
  const [editing, setEditing] = useState<PlanEntry | null>(null);
  const [guestsFor, setGuestsFor] = useState<PlanEntry | null>(null);
  /** Which day the mobile layout is showing. Desktop ignores this. */
  const [focusedDate, setFocusedDate] = useState<string | null>(null);
  const qc = useQueryClient();

  // Deep links from NextCard and GettingStarted: `?date=` focuses a day,
  // `?add=1` opens the picker on arrival. A recommendation should deliver you
  // into the task, not merely onto the screen that contains it.
  const [params, setParams] = useSearchParams();
  const [welcomed, setWelcomed] = useState(false);
  useEffect(() => {
    const date = params.get("date");
    const wantsAdd = params.get("add") === "1";
    const wantsNext = params.get("week") === "next";
    const fromWizard = params.get("welcome") === "1";
    if (!date && !wantsAdd && !wantsNext && !fromWizard) return;
    if (date) setFocusedDate(date);
    if (wantsNext) setWeekOffset(1);
    if (fromWizard) setWelcomed(true);
    if (wantsAdd && date) setAddingTo({ date, slot: null });
    else if (wantsAdd) setAddingTo({ date: date ?? "", slot: null });
    params.delete("date"); params.delete("add"); params.delete("week"); params.delete("welcome");
    setParams(params, { replace: true });
  }, [params, setParams]);

  // The week to show. Anchored on the server's today, never the browser's.
  const range = useMemo(() => {
    if (!today) return null;
    const start = addDays(weekStartFor(today), weekOffset * 7);
    return { from: start, to: addDays(start, 6) };
  }, [today, weekOffset, weekStartFor]);

  const plan = useQuery({
    queryKey: ["plan", range?.from, range?.to],
    queryFn: () => PlanApi.range(range!.from, range!.to),
    enabled: !!range,
  });

  const conflicts = useQuery({
    queryKey: ["plan", "conflicts", range?.from, range?.to],
    queryFn: () => PlanApi.conflicts(range!.from, range!.to),
    enabled: !!range,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["plan"] });
    void qc.invalidateQueries({ queryKey: ["shopping"] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => PlanApi.remove(id),
    onSuccess: invalidate,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      PlanApi.update(id, { status }),
    onSuccess: invalidate,
  });

  const editEntry = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, unknown> }) =>
      PlanApi.update(id, updates),
    onSuccess: invalidate,
  });

  if (dateLoading || !range) return <Loading label="Working out what day it is…" />;
  if (plan.isLoading) return <Loading label="Loading your week…" />;
  if (plan.error) {
    return <ErrorBox message={(plan.error as Error).message} onRetry={() => void plan.refetch()} />;
  }
  if (!plan.data) return null;

  const { days, slots } = plan.data;
  const allergenConflicts = conflicts.data?.conflicts.filter((c) => c.severity === "allergen") ?? [];

  // The mobile day. Defaults to today when today is in the visible week, and to
  // the first day otherwise — so paging to next week lands on Monday, not on a
  // date that isn't there.
  const activeDate = (focusedDate && days.some((d) => d.date === focusedDate))
    ? focusedDate
    : (days.find((d) => d.isToday)?.date ?? days[0]?.date ?? range.from);
  const activeDay = days.find((d) => d.date === activeDate) ?? days[0];

  const plannedThisWeek = days.reduce((n, d) => n + d.entries.length, 0);

  const renderDay = (day: typeof days[number], compact: boolean) => (
    <div
      className={`card flex flex-col p-2 ${
        day.isToday ? "border-accent ring-1 ring-accent" : ""
      } ${day.isPast ? "opacity-60" : ""}`}
    >
      <div className="mb-2 flex items-baseline justify-between px-1">
        <span className={`text-xs font-medium ${day.isToday ? "text-accent" : "text-ink-3"}`}>
          {formatWeekday(day.date)}
        </span>
        <span className={`text-lg ${day.isToday ? "font-semibold text-accent" : ""}`}>
          {formatDayNumber(day.date)}
        </span>
      </div>
      {day.isToday && (
        <span className="mb-1 px-1 text-[10px] uppercase tracking-wide text-accent">Today</span>
      )}

      <div className="flex-1 space-y-2">
        {slots.map((slot) => {
          const entries = day.entries.filter((e) => e.slot_key === slot.key);
          return (
            <div key={slot.key}>
              <div className="px-1 text-[10px] uppercase tracking-wide text-ink-3">
                {slot.label}
              </div>
              {entries.map((e) => (
                <EntryCard
                  key={e.id}
                  entry={e}
                  conflicts={conflicts.data?.conflicts.filter(
                    (c) => c.planEntryId === e.id) ?? []}
                  onDelete={() => remove.mutate(e.id)}
                  onGuests={() => setGuestsFor(e)}
                  onToggleCooked={() => setStatus.mutate({
                    id: e.id,
                    status: e.status === "cooked" ? "planned" : "cooked",
                  })}
                  onEdit={() => setEditing(e)}
                />
              ))}
              <button
                className={`no-print mt-1 flex w-full items-center justify-center gap-1 rounded-lg
                            border border-dashed border-rule text-ink-3 transition
                            hover:border-accent hover:text-accent ${
                  compact ? "px-2 py-1 text-xs" : "px-2 py-2.5 text-sm"}`}
                onClick={() => setAddingTo({ date: day.date, slot: slot.key })}
              >
                <Icon name="plus" className="h-3.5 w-3.5" />
                Add
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Your week"
        subtitle="Pick a day and add a meal. Guests, leftovers and portion sizes are handled for you."
        actions={
          <>
            <ActionMenu
              trigger={<span className="text-sm">Week tools</span>}
              items={[
                {
                  label: "Copy or reuse a week",
                  icon: "copy",
                  hint: "Templates, and copying last week across",
                  onSelect: () => setShowTools(true),
                },
                {
                  label: "Print the week",
                  icon: "printer",
                  onSelect: () => void PlanApi.exportPdf(range.from, range.to),
                },
                {
                  label: "Add to calendar",
                  icon: "calendarPlus",
                  hint: "Downloads an .ics file",
                  onSelect: () => void PlanApi.exportIcs(range.from, range.to),
                },
              ]}
            />
            <button className="btn-primary" onClick={() => setAddingTo({ date: activeDate, slot: null })}>
              <Icon name="plus" className="h-4 w-4" />
              Add a meal
            </button>
          </>
        }
      />

      {/* ── week navigation ─────────────────────────────────────────────── */}
      <div className="no-print mb-3 flex items-center gap-1">
        <button className="btn-ghost !px-2" onClick={() => setWeekOffset((w) => w - 1)}
                aria-label="Previous week">
          <Icon name="chevronLeft" className="h-4 w-4" />
        </button>
        <button className="btn-ghost" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>
          This week
        </button>
        <button className="btn-ghost !px-2" onClick={() => setWeekOffset((w) => w + 1)}
                aria-label="Next week">
          <Icon name="chevronRight" className="h-4 w-4" />
        </button>
        <span className="ml-2 truncate text-sm text-ink-2">{formatRange(range.from, range.to)}</span>
      </div>

      {/* Where the wizard lands. It says what was done and, more importantly,
          gives explicit permission to change it — a drafted week that reads as
          fixed is worse than no week at all. */}
      {welcomed && (
        <div className="mb-3">
          <Banner onDismiss={() => setWelcomed(false)}>
            <strong>Here's your week.</strong> We've drafted a few dinners and built the
            shopping list. Change anything you like — swap a meal, add a day, adjust the
            servings. Nothing here is fixed.
          </Banner>
        </div>
      )}

      {/* Allergen conflicts are deterministic, not advisory — surface them at
          the top of the week rather than only on the individual card. */}
      {allergenConflicts.length > 0 && (
        <div className="mb-3">
          <Banner tone="danger">
            <strong>{allergenConflicts.length} allergen conflict
              {allergenConflicts.length === 1 ? "" : "s"} this week.</strong>
            <ul className="mt-1 list-disc pl-5">
              {allergenConflicts.slice(0, 4).map((c, i) => (
                <li key={i}>
                  {c.recipeTitle} on {c.planDate} contains {c.tag} — a problem for {c.profileName}.
                </li>
              ))}
            </ul>
          </Banner>
        </div>
      )}

      {/* ── mobile: the ribbon jumps, one day is shown ──────────────────── */}
      <div className="lg:hidden">
        <WeekRibbon
          className="mb-3"
          days={days.map((d) => ({
            date: d.date, count: d.entries.length, isToday: d.isToday,
          }))}
          activeDate={activeDate}
          onPick={setFocusedDate}
        />
        {activeDay && renderDay(activeDay, false)}
      </div>

      {/* ── desktop: the whole week at a glance ─────────────────────────── */}
      <div className="hidden gap-2 lg:grid lg:grid-cols-7">
        {days.map((day) => <div key={day.date}>{renderDay(day, true)}</div>)}
      </div>

      {/* ── the hand-off ────────────────────────────────────────────────────
          Planning a week used to end in nothing. The user had filled the grid,
          the app knew exactly what that implied, and said so on a different
          screen the user had to think to visit. */}
      {plannedThisWeek > 0 && (
        <div className="no-print mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rule-soft bg-paper-2 p-4">
          <div>
            <div className="text-sm font-medium text-ink">
              {plannedThisWeek} meal{plannedThisWeek === 1 ? "" : "s"} planned
            </div>
            <div className="text-xs text-ink-3">
              Turn them into one shopping list, combined and sorted by aisle.
            </div>
          </div>
          <Link to={`/shopping?generate=1&from=${range.from}&to=${range.to}`}
                className="btn-ghost shrink-0">
            <Icon name="cart" className="h-4 w-4" />
            Make the shopping list
          </Link>
        </div>
      )}

      {addingTo && (
        <AddEntryModal
          date={addingTo.date || activeDate}
          slot={addingTo.slot}
          days={days.map((d) => ({ date: d.date, isToday: d.isToday }))}
          slots={slots}
          onClose={() => setAddingTo(null)}
          onAdded={() => { setAddingTo(null); invalidate(); }}
        />
      )}
      {editing && (
        <EditEntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={(updates) => {
            editEntry.mutate({ id: editing.id, updates });
            setEditing(null);
          }} />
      )}
      {guestsFor && (
        <GuestsModal
          entry={guestsFor}
          onClose={() => setGuestsFor(null)}
          onSaved={() => { setGuestsFor(null); invalidate(); }}
        />
      )}

      {showTools && (
        <WeekToolsModal weekStart={range.from} weekEnd={range.to}
                        onClose={() => setShowTools(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function EntryCard({ entry, conflicts, onDelete, onGuests, onToggleCooked, onEdit }: {
  entry: PlanEntry;
  conflicts: Array<{ severity: string; tag: string | null; profileName: string | null }>;
  onDelete: () => void;
  onGuests: () => void;
  onToggleCooked: () => void;
  onEdit?: () => void;
}) {
  const name = entry.recipe_title ?? entry.food_name ?? entry.custom_title ?? "—";
  const allergenHits = conflicts
    .filter((c) => c.severity === "allergen")
    .map((c) => c.tag)
    .filter((t): t is string => !!t);

  const isFreeText = entry.custom_title !== null;
  const cooked = entry.status === "cooked";

  return (
    <div
      className={`group mt-1 rounded-lg border px-2 py-1.5 text-sm ${
        allergenHits.length ? "border-danger/40 bg-danger/5" : "border-rule-soft"
      } ${entry.status === "skipped" ? "line-through opacity-50" : ""}`}
    >
      <div className="flex items-start gap-1">
        <span className="min-w-0 flex-1 leading-snug">{name}</span>

        <div className="no-print flex shrink-0 items-start">
          {/* Marking a meal cooked is the single most-used action on this card,
              so it is a permanent toggle rather than one item in a menu — and
              it carries aria-pressed, which the old emoji ○/✓ never did. */}
          <button
            onClick={onToggleCooked}
            aria-pressed={cooked}
            aria-label={cooked ? `Mark ${name} as not cooked` : `Mark ${name} as cooked`}
            title={cooked ? "Cooked" : "Mark cooked"}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${
              cooked ? "text-accent hover:bg-accent/10" : "text-ink-3 hover:bg-paper-3 hover:text-ink"}`}
          >
            <Icon name={cooked ? "check" : "circle"} className="h-4 w-4" />
          </button>

          {/* `.reveal` rather than `opacity-0 group-hover:opacity-100`: visible
              by default, hidden only where a fine pointer exists to bring it
              back. On a phone this menu is simply always there. */}
          <div className="reveal">
            <ActionMenu
              label={`Actions for ${name}`}
              items={[
                {
                  label: "Guests",
                  icon: "users",
                  hint: entry.guest_count > 0
                    ? `${entry.guest_count} currently added`
                    : "Cook extra for visitors",
                  onSelect: onGuests,
                },
                ...(onEdit && isFreeText
                  ? [{ label: "Edit title", icon: "pencil" as const, onSelect: onEdit }]
                  : []),
                {
                  label: "Remove from the plan",
                  icon: "x",
                  destructive: true,
                  onSelect: onDelete,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-ink-3">
        {/* effective_servings, not `servings` — guests are already folded in. */}
        <span>{entry.effective_servings} servings</span>
        {entry.guest_count > 0 && (
          <span className="chip border-accent/30 text-accent">
            +{entry.guest_count} guest{entry.guest_count === 1 ? "" : "s"}
          </span>
        )}
        {entry.leftover_of && <span className="chip">leftovers</span>}
        {entry.recipe_minutes && <span>{entry.recipe_minutes} min</span>}
        {entry.quantity !== null && entry.unit !== null && (
          <span>{entry.quantity}{entry.unit !== "ct" ? ` ${entry.unit}` : ""}</span>
        )}
      </div>

      {allergenHits.length > 0 && (
        <div className="mt-1">
          <AllergenBadge tags={[]} partial={false} conflictsWith={allergenHits} />
        </div>
      )}
      {!allergenHits.length && entry.recipe_allergens_partial && (
        <div className="mt-1">
          <AllergenBadge tags={[]} partial />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a meal.
 *
 * ── WHY IT ASKS WHICH DAY ───────────────────────────────────────────────────
 * It used to be openable only from a per-slot `+ Add`, so day and slot were
 * always already decided by the button you pressed. That works when you know
 * where you're going and is useless as a starting point — which is why the
 * screen had 28 entry points and no primary action. Opened from the header,
 * day and slot are chosen here, in the order a person actually thinks about
 * them: which day, which meal, then what.
 *
 * Opened from a slot, both arrive pre-set and the pickers just reflect that.
 */
function AddEntryModal({ date, slot, days, slots, onClose, onAdded }: {
  date: string;
  /** null when opened from the header — the user picks. */
  slot: string | null;
  days: Array<{ date: string; isToday: boolean }>;
  slots: Array<{ key: string; label: string }>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [tab, setTab] = useState<"recipe" | "custom">("recipe");
  const [q, setQ] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [day, setDay] = useState(date);
  // Dinner is the overwhelmingly common case, and the seeded slot list always
  // contains it — but fall back to the first slot rather than assuming.
  const [slotKey, setSlotKey] = useState(
    slot ?? (slots.find((s) => s.key === "dinner")?.key ?? slots[0]?.key ?? "dinner"),
  );

  const recipes = useQuery({
    queryKey: ["recipes", "picker", q],
    queryFn: () => RecipesApi.list({ q: q || undefined, limit: 30 }),
  });

  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      PlanApi.add({ planDate: day, slotKey, ...body }),
    onSuccess: onAdded,
  });

  return (
    <Modal title="Add a meal" onClose={onClose}>
      {/* ── when ─────────────────────────────────────────────────────────── */}
      <div className="mb-3 space-y-2">
        <div>
          <div className="label-mono mb-1.5">Which day</div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => (
              <button
                key={d.date}
                type="button"
                onClick={() => setDay(d.date)}
                aria-pressed={day === d.date}
                className={`flex min-h-[3rem] flex-col items-center justify-center rounded-lg border text-xs transition ${
                  day === d.date
                    ? "border-accent bg-accent/10 font-medium text-accent"
                    : "border-rule-soft text-ink-2 hover:border-accent/50"}`}
              >
                <span className="label-mono text-[10px]">{formatWeekday(d.date)}</span>
                <span className="text-sm">{formatDayNumber(d.date)}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="label-mono mb-1.5">Which meal</div>
          <div className="flex flex-wrap gap-1.5">
            {slots.map((s) => (
              <button key={s.key} type="button" className="chip-toggle"
                      aria-pressed={slotKey === s.key}
                      onClick={() => setSlotKey(s.key)}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-3 flex gap-1 border-t border-rule-soft pt-3">
        <button className={tab === "recipe" ? "btn-primary" : "btn-ghost"} onClick={() => setTab("recipe")}>
          From a recipe
        </button>
        <button className={tab === "custom" ? "btn-primary" : "btn-ghost"} onClick={() => setTab("custom")}>
          Something else
        </button>
      </div>

      {add.error && <ErrorBox message={(add.error as Error).message} />}

      {tab === "recipe" ? (
        <>
          <input
            className="input mb-3"
            placeholder="Search recipes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search recipes"
            autoFocus
          />
          {recipes.isLoading && <Loading />}
          <div className="max-h-72 space-y-1 overflow-auto">
            {recipes.data?.recipes.map((r: RecipeSummary) => (
              <button
                key={r.id}
                className="w-full rounded-lg border border-rule-soft px-3 py-2 text-left text-sm hover:border-accent"
                onClick={() => add.mutate({ recipeId: r.id })}
                disabled={add.isPending}
              >
                <div className="flex items-center justify-between gap-2">
                  <span>{r.title}</span>
                  <span className="text-xs text-ink-3">{r.last_cooked_label}</span>
                </div>
                <div className="mt-0.5 flex gap-1">
                  <AllergenBadge tags={r.allergen_tags} partial={r.allergens_partial} />
                </div>
              </button>
            ))}
            {recipes.data?.recipes.length === 0 && (
              // This was the worst empty state in the app: it appeared INSIDE a
              // modal and said "Add one from the Recipes tab first", so the way
              // out was to dismiss the dialog and go find a tab yourself. The
              // link now goes straight there and opens the add sheet on arrival.
              <Empty
                title="No recipes yet"
                hint="You'll need at least one before you can plan a meal from it."
                action={
                  <>
                    <Link to="/recipes?add=1" className="btn-primary" onClick={onClose}>
                      <Icon name="plus" className="h-4 w-4" />
                      Add a recipe
                    </Link>
                    <button className="btn-ghost" onClick={() => setTab("custom")}>
                      Or just type what you're eating
                    </button>
                  </>
                }
              />
            )}
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (customTitle.trim()) add.mutate({ customTitle: customTitle.trim() });
          }}
        >
          <input
            className="input"
            placeholder="Takeout, dinner at Mum's, leftovers…"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            autoFocus
          />
          <p className="mt-2 text-xs text-ink-3">
            Free-text meals contribute nothing to the shopping list.
          </p>
          <button className="btn-primary mt-3" disabled={!customTitle.trim() || add.isPending}>
            Add
          </button>
        </form>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function GuestsModal({ entry, onClose, onSaved }: {
  entry: PlanEntry; onClose: () => void; onSaved: () => void;
}) {
  const [count, setCount] = useState(entry.guest_count);
  const [servings, setServings] = useState(entry.guest_servings);
  const [note, setNote] = useState(entry.guest_note ?? "");
  const [linked, setLinked] = useState(entry.guest_count === entry.guest_servings);

  const save = useMutation({
    mutationFn: () => PlanApi.setGuests(entry.id, {
      guestCount: count,
      guestServings: linked ? count : servings,
      guestNote: note || null,
    }),
    onSuccess: onSaved,
  });

  const effective = entry.servings + (linked ? count : servings);

  return (
    <Modal title="Guests for this meal" onClose={onClose}>
      {save.error && <ErrorBox message={(save.error as Error).message} />}
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">How many extra people?</span>
          <input
            type="number" min={0} max={100} className="input mt-1"
            value={count}
            onChange={(e) => {
              const n = Math.max(0, Number(e.target.value) || 0);
              setCount(n);
              if (linked) setServings(n);
            }}
            autoFocus
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={linked} onChange={(e) => setLinked(e.target.checked)} />
          They eat a full portion each
        </label>

        {!linked && (
          <label className="block">
            <span className="text-sm">Extra servings to cook</span>
            <input
              type="number" min={0} step={0.5} className="input mt-1"
              value={servings}
              onChange={(e) => setServings(Math.max(0, Number(e.target.value) || 0))}
            />
            {/* Three small children are not three adult portions — which is
                exactly why count and servings are separate. */}
            <p className="mt-1 text-xs text-ink-3">
              Useful for children: 3 guests might only be 1.5 servings.
            </p>
          </label>
        )}

        <label className="block">
          <span className="text-sm">Note (optional)</span>
          <input
            className="input mt-1" placeholder="Sarah + kids"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <div className="rounded-lg bg-accent-soft p-3 text-sm text-accent">
          Cooking for <strong>{effective}</strong> · the shopping list will scale to match.
        </div>

        <button className="btn-primary w-full" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function EditEntryModal({ entry, onClose, onSave }: {
  entry: PlanEntry; onClose: () => void; onSave: (updates: Record<string, unknown>) => void;
}) {
  const [customTitle, setCustomTitle] = useState(entry.custom_title ?? "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ customTitle: customTitle.trim() });
  };

  return (
    <Modal title="Edit meal" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="text-sm">Title</span>
          <input
            className="input mt-1"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            autoFocus
          />
        </label>
        <div className="flex gap-2">
          <button className="btn-ghost flex-1" onClick={onClose}>Cancel</button>
          <button className="btn-primary flex-1" type="submit" disabled={!customTitle.trim()}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
